"use client";

// React Required
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Image from "next/image";

// UI Components
import { Box } from "@mui/material";

// Internal Lib
import {
  Input,
  Label,
  Textarea,
  showToast,
  Radio,
  Dropdown,
} from "@int-traderjoes/ui-components-library";

// File Management
import { useLibraryComponents } from "@/hooks/useLibraryComponents";
import useFileManagement from "@/hooks/useFileManagement";

// Contexts
import { useFormContext } from "@/context/FormContext";
import { useTheme } from "@/context";
import { useLanguageContext } from "@/context/LanguageContext";
import { useGlobalStateContext } from "@/context/GlobalContext";
import ConfirmationBox from "@/components/ConfirmationBox";

// constants
import Constants from "@/static/constants/constants.json";
import { isVendorFieldsLockedForExistingVendor } from "@/utils/vendor/existingVendorFieldsLock";

// Utils
import FormHeader from "@/utils/FormHeader";
import { shouldShowIngredientMultipackFields } from "./utils/helper";
import { resolveVendorAssessmentProductSpecificationType } from "@/components/VendorAssessmentForm/utils/helper";
import { processIngredientListPaste } from "@/utils/textareaHelpers";
import {
  getFileCategory,
  decodeHtmlEntities,
  RequiredDocumentsValidateFile,
  sanitizeVendorCorporateName,
} from "@/utils/helper";

// Custom Components
import { UpArrow, DownArrow } from "@/assets/images/CommonSvgUtils";

// Services
import AuthService from "@/services/authService";
import VendorGuestService from "@/services/vendorGuestService";
import { vendorService } from "@/services";

// Assets
import helpIcon from "@/assets/images/ideation-phase-images/HelpIcon.svg";
import unAcceptableIcon from "@/assets/images/ideation-phase-images/UnAcceptableIcon.svg";

// components
import CustomTooltip from "@/components/Tooltip";

// context imports
import { IIngredientDetailsFormData, IVendorInformationFormData } from "@/context/data.types";

// custom hooks
import { handleApiError } from "@/hooks/useApiErrorHandler";
import { useFileValidator } from "@/hooks/useValidateFile";
import { FILE_VALIDATION_CONFIG } from "@/utils/fileValidationConstants";

// Styles
import "./ingredient-details-form.scss";

interface IngredientData {
  unacceptable: string[];
  avoidIfPossible?: string[];
}
interface IngredientDetailsFormProps {
  vendorKey?: number;
  isVendor?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  formUpdated: () => void;
  isFormDisabled?: boolean;
  isMultipackDisabled?: boolean;
  vendorId?:string
  isNoNFPDocument?:boolean
  defaultmultipackGiftPack?: boolean;
  ingredientFieldsToBeDisabled?: string[];
  isUnacceptableIngredientsHidden?: boolean;
  downloadOnly?: boolean;
  /** When true with isVendor, use TJ/vendor file and ingredient-guide APIs instead of guest endpoints */
  useAuthenticatedIngredientApis?: boolean;
  hasExistingVendor?: boolean | null;
  productSpecificationType?: string | null;
}

const IngredientDetailsForm: React.FC<IngredientDetailsFormProps> = ({
  vendorKey,
  isVendor,
  onToggle,
  isExpanded,
  formUpdated,
  isFormDisabled,
  isMultipackDisabled,
  vendorId,
  isNoNFPDocument,
  defaultmultipackGiftPack,
  ingredientFieldsToBeDisabled,
  isUnacceptableIngredientsHidden,
  downloadOnly,
  useAuthenticatedIngredientApis,
  hasExistingVendor,
  productSpecificationType,
}) => {
  const effectiveProductSpecificationType = useMemo(
    () =>
      resolveVendorAssessmentProductSpecificationType(
        productSpecificationType
      ),
    [productSpecificationType]
  );

  const showMultipackFields = shouldShowIngredientMultipackFields(
    effectiveProductSpecificationType
  );
  const useGuestIngredientFileAndGuideApis =
    Boolean(isVendor) && !useAuthenticatedIngredientApis;
  const { darkMode } = useTheme();
  const theme = darkMode ? "dark" : "light";
  const { setIsLoading } = useGlobalStateContext();
  const [fileSelected, setFileSelected] = useState<File | null>();
  const [fileInputKey, setFileInputKey] = useState<number>(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // Store ingredientCompliance value to preserve across async operations
  const [preservedCompliance, setPreservedCompliance] = useState<string | null | undefined>(undefined);
  
  const {
    ingredientDetailsForm,
    setIngredientDetailsForm,
    ingredientDetailsFormErrors,
    setIngredientDetailsFormErrors,
    isVendorAssessmentSubmitClicked,
    vendorInformationForms,
    setVendorInformationForms,
    setVendorAssessmentForm,
    setVendorContactInformationForm,
  } = useFormContext();
  
  // File management for NFP document
  const { FileUploadDisplay } = useLibraryComponents();
  const documentFields = useMemo(() => ['nfpDocument'], []);
  
  const {
    uploadFile,
    downloadFile,
    removeFile,
    getAllFilesForPayload,
    isUploading,
    hasFile,
    getFileName,
    populateExistingFiles,  
    clearAllFiles,
  } = useFileManagement({
    productId: vendorId || 'unknown',
    variantId: 'nfp',
    documentFields,
    onFileStateChange: useCallback((fieldName: string, fileData: any) => {
      console.log(`🔄 [IngredientDetailsForm] NFP file state changed:`, fileData);
      
      // Update ingredient details form - PRESERVE ingredientCompliance in all cases
      setIngredientDetailsForm(prevData => {
        // Use preserved value if available, otherwise use previous state
        const complianceToPreserve = (preservedCompliance !== undefined && preservedCompliance !== null && preservedCompliance !== "")
          ? preservedCompliance
          : prevData?.ingredientCompliance;
        
        if (fileData && fileData.id) {
          // File uploaded - update with new file data
          return {
            ...prevData,
            nfp: {
              id: fileData.id,
              filename: fileData.filename,
              type: getFileCategory('application/pdf'), // Default type
              data: "", // No base64 data with asset API
            },
            // Preserve ingredientCompliance if it had a value
            ...(complianceToPreserve !== undefined && complianceToPreserve !== null && complianceToPreserve !== ""
              ? { ingredientCompliance: complianceToPreserve }
              : {})
          };
        } else {
          // File removed - clear NFP but preserve ingredientCompliance
          return {
            ...prevData,
            nfp: {
              filename: "",
              type: "",
              data: "",
              id: ""
            },
            // Preserve ingredientCompliance if it had a value
            ...(complianceToPreserve !== undefined && complianceToPreserve !== null && complianceToPreserve !== ""
              ? { ingredientCompliance: complianceToPreserve }
              : {})
          };
        }
      });
      
      if (fileData && fileData.id) {
        formUpdated();
      }
    }, [setIngredientDetailsForm, formUpdated, preservedCompliance]),
          uploadOptions: useMemo(() => ({
            maxSize: 40 * 1024 * 1024, // 40MB
            allowedTypes: [],
            allowedExtensions: [],
            isGuest: useGuestIngredientFileAndGuideApis,
          }), [useGuestIngredientFileAndGuideApis]),
    isGuest: useGuestIngredientFileAndGuideApis,
  });
  
  // Update preserved compliance whenever ingredientCompliance has a value in the form
  useEffect(() => {
    const currentCompliance = ingredientDetailsForm?.ingredientCompliance;
    if (currentCompliance !== undefined && currentCompliance !== null && currentCompliance !== "") {
      setPreservedCompliance(currentCompliance);
    }
  }, [ingredientDetailsForm?.ingredientCompliance]);
  
  const { languageText } = useLanguageContext();
  const ingredientDetailsLabels = languageText?.LABELS?.VENDOR_INFORMATION?.INGREDIENT_DETAILS_FORM;
  const ingredientDetailsFormInfoIconMessages =
    languageText?.MESSAGES?.INFO_ICON_MESSAGES?.VENDOR_INFORMATION?.INGREDIENT_DETAILS_FORM;
  const formHeader = languageText?.TABLE_AND_FORM_HEADER?.FORM_HEADER?.INGREDIENT_DETAILS;
  const toastMessages = languageText?.MESSAGES?.TOAST_MESSAGES;
  const validationMessages = languageText?.MESSAGES?.VALIDATION_MESSAGES;
  const buttonTexts = languageText?.BUTTONS;
  const confirmationBoxMessages = languageText?.MESSAGES?.CONFIRMATION_BOX_MESSAGES;
  const deleteDocumentConfirmTitle =
    confirmationBoxMessages?.CONFIRM_TITLE?.VENDOR_LIBRARY_DOCUMENT_DELETE_CONFIRM_TITLE ||
    "Delete Document";
  const deleteDocumentConfirmMessage =
    confirmationBoxMessages?.CONFIRM_MESSAGE?.VENDOR_LIBRARY_DOCUMENT_DELETE_CONFIRM_MSG ||
    "Are you sure you want to delete?";

  const [ingredientData, setIngredientData] = useState<IngredientData>({
    unacceptable: [],
    avoidIfPossible: [],
  });
 
  let ingredientDetailsFormPermissions: any = {};

  // Fetching permissions for fields
  ingredientDetailsFormPermissions = AuthService.fetchPermissions("IngredientDetails");

  useEffect(() => {
    if(vendorKey !== undefined){
      const isMultiPackProduct = vendorInformationForms[vendorKey ?? 0]?.multipack?.giftPack ? "yes" : "no";
      setIngredientDetailsForm(prev => ({
        ...prev,
        isMultiPackProduct: isMultiPackProduct,
      }));
    }
  }, [setIngredientDetailsForm, vendorInformationForms, vendorKey]);

  const validateFile = useFileValidator({
    allowedTypes: ["*"],
  });

  const getMultiPackIngredientList = (specsOverride?: any[]): string[] => {
    const source = specsOverride ?? ingredientDetailsForm?.productspecifications;
    return (source || [])
      .filter(item => !item?.delete)
      .flatMap(item => {
         // Only return ingredientList if it exists and is a non-empty string
      return item.ingredientList && typeof item.ingredientList === 'string' && item.ingredientList.trim() 
          ? [item.ingredientList] 
          : [];
      });
  };

  const handleValidateIngredient = (specsOverride?: any[]) => {
    const combinedList = [
      ...(ingredientData.unacceptable || []),
      ...(ingredientData.avoidIfPossible || []),
    ];

    const unacceptableSet = new Set(combinedList.map(item => item?.toLowerCase()));
    const categorizedData: {
      unacceptable: string[];
      complianceStatus: string | null;
    } = {
      unacceptable: [],
      complianceStatus: "",
    };
    // Sets to track unique ingredients
    const seenUnacceptable = new Set<string>();
    let showError = false;
    // Always derive ingredient list from productspecifications (prefer override snapshot if provided)
    const ingredientList: string[] = getMultiPackIngredientList(specsOverride);
      // Filter out null/undefined/empty values before processing
      const validIngredientList = (ingredientList || []).filter(
        ingredient => ingredient && typeof ingredient === 'string' && ingredient.trim()
      );

      validIngredientList?.forEach(ingredient => {
      let normalizedIngredient = ingredient.replace(/\s+/g, " ").trim();

      // Capitalize the first letter of each word
      normalizedIngredient = normalizedIngredient
        .split(" ")
        .map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase())
        .join(" ");

      if (!normalizedIngredient || (!normalizedIngredient.includes(",") && normalizedIngredient.split(" ").length > 1 && !showError)) {
        showError = true;
      }

      // Convert to lowercase and split if multiple ingredients exist
      const lowerCaseIngredients = normalizedIngredient
        ?.toLowerCase()
        .split(",")
        .map(item => item.trim())
        .filter(item => item.length > 0);  // Filter out empty strings from split

      lowerCaseIngredients.forEach(lowerCaseIngredient => {
        if (unacceptableSet.has(lowerCaseIngredient)) {
          if (!seenUnacceptable.has(lowerCaseIngredient)) {
            categorizedData.unacceptable.push(lowerCaseIngredient);
            seenUnacceptable.add(lowerCaseIngredient);
          }
        }
      });
    });

    if (validIngredientList.length > 0) {
      categorizedData.complianceStatus = categorizedData.unacceptable.length
        ? "Ingredient_Concern"
        : "Ingredient_Approved";
    } else {
      categorizedData.complianceStatus = null;
    }
    setIngredientDetailsForm(prev => ({
      ...prev,
      unacceptableIngredients: categorizedData.unacceptable.join(","),
      ingredientCompliance: categorizedData.complianceStatus,
    }));
    if (showError) {
      showToast({
        type: "error",
        message: "Please enter ingredients list in comma separated values",
      });
    }
  };
 
  // Input Change Handler
  const handleInputChange =
    (field: keyof typeof ingredientDetailsForm, index: number = 0) =>
    (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
      let newValue = e.target.value.replace(/\s{2,}/g, " ");

      // Sanitize vendorName to prevent characters that could break base64 encoding/decoding
      if (field === "vendorName") {
        newValue = sanitizeVendorCorporateName(newValue);
      }

      if (field === "vendorName") {
        if (vendorKey === undefined) {
          vendorKey = 0;
        }
        setVendorInformationForms(prevData =>
          prevData.map((vendorForm, index) =>
            index === vendorKey
              ? {
                  ...vendorForm,
                  vendorCorporateName: newValue,
                }
              : vendorForm
          )
        );
        setVendorAssessmentForm(prevData => ({
          ...prevData,
          [field]: newValue,
        }));
        setVendorContactInformationForm(prevData => ({
          ...prevData,
          [field]: newValue,
        }));
        // Keep ingredientDetailsForm in sync with input value
        setIngredientDetailsForm(prevData => ({
          ...prevData,
          vendorName: newValue,
        }));
      } else if (
        field === "productName" ||
        field === "ingredientList" ||
        field === "subIngredientList"
      ) {
        const productSpecification = [...ingredientDetailsForm.productspecifications];
        const existingSpec = productSpecification[index] ?? {
          ingredientList: "",
          subIngredientList: "",
          productName: "",
          productSpecificationName: "",
        };
        productSpecification[index] = { ...existingSpec, [field]: newValue } as any;
        setIngredientDetailsForm(prevData => ({
          ...prevData,
          productspecifications: [...productSpecification],
        }));

        if (field === "ingredientList" || field === "subIngredientList") {
          setIngredientDetailsForm(prevData => ({
            ...prevData,
            [field]: field === "ingredientList" ? [newValue ?? null] : newValue,
          }));
        }
      } else {
        setIngredientDetailsForm(prevData => ({
          ...prevData,
          [field]: newValue,
        }));
      }
    
      formUpdated();
    };

  const handleIngredientPaste = (index?: number) => (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    
    const pastedText = e.clipboardData.getData('text');
    const target = e.currentTarget;
    const newValue = processIngredientListPaste(
      pastedText,
      target.value,
      target.selectionStart,
      target.selectionEnd
    );
    
    // Truncate to maxLength (1000 characters)
    const truncatedValue = newValue.substring(0, 1000);
    
    // Directly update the form state through the onChange handler
    const syntheticEvent = {
      target: { value: truncatedValue }
    } as React.ChangeEvent<HTMLTextAreaElement>;
    
    if (index !== undefined) {
      // For multi-pack ingredient lists
      handleInputChange("ingredientList", index)(syntheticEvent);
    } else {
      // For single ingredient list
      handleInputChange("ingredientList")(syntheticEvent);
    }
    
  };

  // File validation configuration for NFP (multiple types)
  const { allowedTypes: nfpAllowedTypes, toastMessage: nfpToastMessage } = FILE_VALIDATION_CONFIG.NFP_DOCUMENTS;

  const handleFileChange = useCallback(async (file: File | null) => {
    const fieldName = 'nfpDocument'; // Fixed field name for NFP
    
    try {
      if (file === null) {
        // Remove file
        console.log(`🗑️ [IngredientDetailsForm] Removing NFP file`);
        
        // Capture ingredientCompliance from current form state BEFORE deletion and store it
        const currentIngredientCompliance = ingredientDetailsForm?.ingredientCompliance;
        if (currentIngredientCompliance !== undefined && currentIngredientCompliance !== null && currentIngredientCompliance !== "") {
          setPreservedCompliance(currentIngredientCompliance);
        }
        
        await removeFile(fieldName);
        
        // Clear form data - PRESERVE ingredientCompliance from stored value or previous state
        setIngredientDetailsForm(prevData => {
          // Use preserved value if available, otherwise use previous state
          const complianceToPreserve = (preservedCompliance !== undefined && preservedCompliance !== null && preservedCompliance !== "")
            ? preservedCompliance
            : prevData?.ingredientCompliance;
          
          return {
            ...prevData,
            nfp: {
              filename: "",
              type: "",
              data: "",
              id: ""
            },
            // Preserve ingredientCompliance if it had a value
            ...(complianceToPreserve !== undefined && complianceToPreserve !== null && complianceToPreserve !== ""
              ? { ingredientCompliance: complianceToPreserve }
              : {})
          };
        });
        
        // Update UI state
        setFileSelected(null);
        setFileInputKey(prevKey => prevKey + 1);
        formUpdated();
        return;
      }

      // Upload flow
      if (!file) return;
      
      if (!RequiredDocumentsValidateFile(file, validationMessages, validationMessages?.ONLY_NFP_PDF_ALLOWED_ERROR, [])) {
        setFileSelected(null);
        setFileInputKey((prevKey) => prevKey + 1); // Change key to force re-render
        return;
      }

      if (!validateFile(file)) {
        setFileInputKey(prevKey => prevKey + 1);
        return;
      }

      console.log(`🔄 [IngredientDetailsForm] Uploading NFP file:`, file.name);
      setFileSelected(file);
      
      // Generate custom path for NFP document
      const customPath = `vendor-assessment/${vendorId || 'unknown'}/nfp/${file.name}`;
      
      try {
        setIsLoading(true); // Show global loader
        await uploadFile(fieldName, file, customPath);
      } finally {
        // Always hide loader, even if upload fails
        setIsLoading(false);
      }
      
    } catch (error) {
      console.error(`❌ [IngredientDetailsForm] Error handling NFP file:`, error);
      setFileSelected(null);
      setFileInputKey(prevKey => prevKey + 1);
      // Toast is already shown by useFileManagement hook — no duplicate error toast here.
    }
  }, [uploadFile, removeFile, validateFile, vendorId, setIngredientDetailsForm, formUpdated, setIsLoading]);

  const handleRadioChange =
  (field: keyof typeof ingredientDetailsForm) => (value: string) => {
    setIngredientDetailsForm((prevData) => {
      let updatedProductSpecifications = prevData.productspecifications;
      let updatedIngredientList = prevData.ingredientList;
      // #region isMultiPackProduct logic
      if (field === "isMultiPackProduct") {
        if (value === "no") {
          // Switching from Yes to No (Multipack → Single pack)
          // Requirement: Mark all id ones to delete:true, remove all other objects
          // Case 1 (after save): mark all saved ones delete:true, create new object
          // Case 2 (before save): remove all except one productSpecification object
          
          const savedSpecs = prevData.productspecifications?.filter((spec: any) => Boolean(spec?.id)) || [];
          const unsavedSpecs = prevData.productspecifications?.filter((spec: any) => !spec?.id) || [];
          
          const singleTemplate = {
            productName: "",
            ingredientList: "",
            subIngredientList: "",
            delete: false
          };
          
          if (savedSpecs.length > 0) {
            // Case 1: After save - mark all saved ones delete:true, create new single object
            const savedMarkedDelete = savedSpecs.map((spec: any) => ({ ...spec, delete: true }));
            updatedProductSpecifications = [singleTemplate, ...savedMarkedDelete];
          } else {
            // Case 2: Before save - remove all except one productSpecification object
            const firstUnsaved = unsavedSpecs[0] || singleTemplate;
            updatedProductSpecifications = [{ ...firstUnsaved, delete: false }];
          }
          
          // For single product mode, clear the legacy top-level ingredientList array;
          // the UI reads from productspecifications[0]
          updatedIngredientList = [];
            
        } else if (value === "yes") {
          // Switching from No to Yes (Single pack → Multipack)
          // If pack count is not selected, mark all saved specs delete:true and drop unsaved ones
          const currentCount =
            Number(vendorInformationForms[vendorKey ?? 0]?.multipack?.amountOfProductsInGiftPack ?? 0);

          if (!currentCount) {
            const savedOnly = (prevData.productspecifications || []).filter((spec: any) => Boolean(spec?.id));
            updatedProductSpecifications = savedOnly.map((spec: any) => ({ ...spec, delete: true }));
          } else {
            // Pack count is selected - unmark deletion on saved specs
            updatedProductSpecifications = prevData.productspecifications.map((spec: any) =>
              spec?.id ? { ...spec, delete: false } : spec
            );
          }

          // Clear single ingredient list for multipack flow
          updatedIngredientList = [];
        }
      }

      return {
        ...prevData,
        [field]: value,
        productspecifications: updatedProductSpecifications,
        ingredientList: updatedIngredientList,
      };
    });

    vendorKey = vendorKey ?? 0
    setVendorInformationForms((prevData) =>
      prevData.map((vendorForm) => { 
        return ({
            ...vendorForm,
            multipack: {
              ...vendorForm.multipack,
              giftPack: value === "yes",
              amountOfProductsInGiftPack: value === "yes" ? vendorForm.multipack.amountOfProductsInGiftPack : 0
            }
          })
        }
      )
    );
    setIngredientDetailsFormErrors((prev) => { return { 
      ...prev,
      ingredientDetailsError: prev.ingredientDetailsError?.filter(i => i !== field),
      productspecification: prev.productspecification
   }})
    // Build a snapshot for validation to avoid stale state reads
    let specsForValidation: any[] | undefined;
    if (field === "isMultiPackProduct") {
      const currentSpecs = ingredientDetailsForm.productspecifications || [];
      const savedSpecs = currentSpecs.filter((spec: any) => Boolean(spec?.id));
      const unsavedSpecs = currentSpecs.filter((spec: any) => !spec?.id);
      const singleTemplate = {
        productName: "",
        ingredientList: "",
        subIngredientList: "",
        delete: false
      };
      if (value === "no") {
        if (savedSpecs.length > 0) {
          const savedMarkedDelete = savedSpecs.map((spec: any) => ({ ...spec, delete: true }));
          specsForValidation = [singleTemplate, ...savedMarkedDelete];
        } else {
          const firstUnsaved = unsavedSpecs[0] || singleTemplate;
          specsForValidation = [{ ...firstUnsaved, delete: false }];
        }
      } else if (value === "yes") {
        const currentCount =
          Number(vendorInformationForms[vendorKey ?? 0]?.multipack?.amountOfProductsInGiftPack ?? 0);
        if (!currentCount) {
          // No pack count selected - keep only saved ones marked for deletion
          const savedOnly = currentSpecs.filter((spec: any) => Boolean(spec?.id));
          specsForValidation = savedOnly.map((spec: any) => ({ ...spec, delete: true }));
        } else {
          specsForValidation = currentSpecs.map((spec: any) => (spec?.id ? { ...spec, delete: false } : spec));
        }
      }
    }
    handleValidateIngredient(specsForValidation);
    formUpdated();
  };

  const handleDropdownChange =
    (field: keyof typeof ingredientDetailsForm) =>
    (e: React.ChangeEvent<HTMLInputElement>, option: any) => {
      setIngredientDetailsForm(prevData => ({
        ...prevData,
        [field]: option?.value || "",
      }));

      vendorKey = vendorKey ?? 0;
      // The dropdown shows the count of the active multipack form (see getMultipackCount), which may not be the
      // form at vendorKey. When clearing, also reset that displayed form (only that one), otherwise the old value
      // keeps being displayed and the field looks like it cannot be unselected.
      const isClearingPackCount = field === "howManyProductsInPack" && !option?.value;
      setVendorInformationForms(prevData => {
        const displayedIndex = isClearingPackCount
          ? prevData.findIndex(i => Number(i?.multipack?.amountOfProductsInGiftPack) > 0)
          : -1;
        return prevData.map((vendorForm, index) =>
          index === vendorKey || index === displayedIndex
            ? {
                ...vendorForm,
                multipack: {
                  ...vendorForm.multipack,
                  amountOfProductsInGiftPack: option?.value || null,
                },
              }
            : vendorForm
        );
      });
      let specsForValidation: any[] | undefined;
      // #region howManyProductsInPack logic
      if (field === "howManyProductsInPack") {
        const newCount = Number(option?.value);
        const currentSpecs = ingredientDetailsForm.productspecifications || [];
        
        // Separate saved (with id) and unsaved (no id) specs
        const savedSpecs = currentSpecs.filter((spec: any) => Boolean(spec?.id));
        const unsavedSpecs = currentSpecs.filter((spec: any) => !spec?.id);
        const savedCount = savedSpecs.length;

        if (newCount < savedCount) {
          // Case 1: Pack count less than saved product specification count
          // - Remove all unsaved objects from product specification
          // - Mark the extra saved ones (that is already saved in BE) as delete: true
          
          // Keep first newCount saved specs (unmark delete if needed)
          const keptSavedSpecs = savedSpecs.slice(0, newCount).map((spec: any) => ({
            ...spec,
            delete: false
          }));
          
          // Mark remaining saved specs as delete:true
          const extraSavedSpecs = savedSpecs.slice(newCount).map((spec: any) => ({
            ...spec,
            delete: true
          }));
          
          // Remove all unsaved objects, keep only saved ones (kept + marked delete)
          const finalProductSpecifications = [...keptSavedSpecs, ...extraSavedSpecs];
          
          setIngredientDetailsForm(prevData => ({
            ...prevData,
            productspecifications: finalProductSpecifications,
          }));
          specsForValidation = finalProductSpecifications;
        } else if (newCount > savedCount) {
          // Case 2: Pack count greater than saved product specification count
          // - Add the newly ones in product specification objects
          
          // Keep all saved specs (unmark delete if needed)
          const keptSavedSpecs = savedSpecs.map((spec: any) => ({
            ...spec,
            delete: false
          }));
          
          // Keep unsaved specs that are within the new count
          const keptUnsavedSpecs = unsavedSpecs.slice(0, newCount - savedCount);
          
          // Add new objects to fill up to newCount
          const missing = newCount - (savedCount + keptUnsavedSpecs.length);
          const template = {
            productName: "",
            ingredientList: "",
            subIngredientList: "",
            delete: false
          };
          const newSpecs = missing > 0 ? Array.from({ length: missing }, () => ({ ...template })) : [];
          
          const finalProductSpecifications = [...keptSavedSpecs, ...keptUnsavedSpecs, ...newSpecs];
          
          setIngredientDetailsForm(prevData => ({
            ...prevData,
            productspecifications: finalProductSpecifications,
          }));
          specsForValidation = finalProductSpecifications;
        } else {
          // newCount === savedCount: Just ensure all saved specs are not marked for deletion
          const updatedSpecs = savedSpecs.map((spec: any) => ({
            ...spec,
            delete: false
          }));
          
          // Remove any unsaved specs since count matches saved count
          setIngredientDetailsForm(prevData => ({
            ...prevData,
            productspecifications: updatedSpecs,
          }));
          specsForValidation = updatedSpecs;
        }
      } 
      setIngredientDetailsFormErrors((prev) => { return { 
        ...prev,
        ingredientDetailsError: prev.ingredientDetailsError?.filter(i => i !== field),
        productspecification: prev.productspecification
     }})
      handleValidateIngredient(specsForValidation);
      formUpdated();
    };
    
  const handleFieldFocus = (field: keyof IIngredientDetailsFormData, index: number) => {
  if(field === 'ingredientList' || field === 'productName'){ 
    setIngredientDetailsFormErrors(prevErrors =>{ 
      return {
        ingredientDetailsError: prevErrors.ingredientDetailsError,
        productspecification: {
            ...prevErrors.productspecification,
            [index]: prevErrors.productspecification?.[index]?.filter(errorfield => errorfield !== field) || []
          }
        };
      });
    } else {
      setIngredientDetailsFormErrors((prev) => { 
        return { 
          ...prev,
          ingredientDetailsError: prev.ingredientDetailsError?.filter(i => i !== field),
          productspecification: prev.productspecification
        };
      });
    }
  };

  const handleBlur = (field: keyof IIngredientDetailsFormData, index: number) => {
    // Add to vendorAssessmentFormErrors if the field is empty on blur
    if (isVendorAssessmentSubmitClicked) {
      // Special handling for ingredientList which is tracked per productspecification index
      if (field === "ingredientList") {
        const specListValue =
          ingredientDetailsForm?.productspecifications?.[index]?.ingredientList || "";
        const isEmpty = !specListValue || !String(specListValue).trim();
        if (isEmpty && ingredientDetailsFormPermissions[field].isMandatory) {
          setIngredientDetailsFormErrors(prev => {
            const currentIndexErrors = prev.productspecification?.[index] || [];
            const hasError = currentIndexErrors.includes("ingredientList");
            return {
              ...prev,
              productspecification: {
                ...prev.productspecification,
                [index]: hasError
                  ? currentIndexErrors
                  : [...currentIndexErrors, "ingredientList"],
              },
            };
          });
        }
      } else if (
        (!ingredientDetailsForm[field] ||
          (Array.isArray(ingredientDetailsForm[field]) &&
            ingredientDetailsForm[field].length === 0)) &&
        ingredientDetailsFormPermissions[field].isMandatory
      ) {
        setIngredientDetailsFormErrors(prev => ({
          ...prev,
          ingredientDetailsError: [...(prev.ingredientDetailsError || []), field],
          productspecification: prev.productspecification,
        }));
      }
    }
    if (field === "ingredientList") {
      // Sync ingredientList array from productspecifications[0]?.ingredientList for backward compatibility
      const isMultiPack = vendorInformationForms[vendorKey ?? 0]?.multipack?.giftPack ?? false;
      
      // Validate - handleValidateIngredient reads from productspecifications[0]?.ingredientList for single product
      const ingredientListString = ingredientDetailsForm?.productspecifications[0]?.ingredientList || "";
      if (ingredientListString || (isMultiPack && ingredientDetailsForm?.ingredientList.length)) {
        handleValidateIngredient();
      }
    }
  };

  const getIngredientGuide = async () => {
    if (useGuestIngredientFileAndGuideApis) {
      try {
        const resp = await VendorGuestService.getGuestIngredientGuideData();
        // Decode HTML entities in ingredient guide data before setting state
        const decodedResp = decodeHtmlEntities(resp);
        setIngredientData(decodedResp);
        console.log("fetched ingredients guide", decodedResp);
      } catch (error) {
        handleApiError(error, toastMessages?.FETCH_INGREDIENT_GUIDE_ERROR);
        console.log("Error fetching ingredient guide");
      }
    } else {
      try {
        const resp = await vendorService.getIngredientGuideData();
        // Decode HTML entities in ingredient guide data before setting state
        const decodedResp = decodeHtmlEntities(resp);
        setIngredientData(decodedResp);
        console.log("fetched ingredients guide", decodedResp);
      } catch (error) {
      handleApiError(error, toastMessages?.FETCH_INGREDIENT_GUIDE_ERROR);
        console.log("Error fetching ingredient guide");
      }
    }
  };

  const handleDeleteFile = useCallback(async () => {
    await handleFileChange(null);
  }, [handleFileChange]);

  const handleRequestDeleteFile = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const handleCancelDeleteFile = useCallback(() => {
    setDeleteConfirmOpen(false);
  }, []);

  const handleConfirmDeleteFile = useCallback(() => {
    setDeleteConfirmOpen(false);
    void handleDeleteFile();
  }, [handleDeleteFile]);

  const handleDownloadFile = useCallback(async () => {
    const fieldName = 'nfpDocument';
    try {
      await downloadFile(fieldName);
    } catch (error) {
      console.error("Failed to download NFP file:", error);
      handleApiError(
        error,
        toastMessages?.FILE_VALIDATION?.FAILED_DOWNLOAD_FILE_ERROR ||
          "Failed to download file. Please try again."
      );
    }
  }, [downloadFile]);

  const renderNutritionFactPanel = () => {
    const fieldName = 'nfpDocument';
    const hasUploadedFile = hasFile(fieldName);
    const fileName = getFileName(fieldName);
    const isUploadingFile = isUploading(fieldName);

    // Check for NFP file from both sources: file management hook AND form data
    const hasNfpFromFormData = !!(ingredientDetailsForm?.nfp?.id && ingredientDetailsForm?.nfp?.filename);
    const hasUploadedFileWithFormData = hasUploadedFile || hasNfpFromFormData;
    const fileNameWithFormData = fileName || ingredientDetailsForm?.nfp?.filename;
    

    return (
      <FileUploadDisplay
        hasUploadedFile={hasUploadedFileWithFormData}
        fileName={fileNameWithFormData}
        isUploading={isUploadingFile}
        uploadComplete={!isUploadingFile && hasUploadedFileWithFormData}
        onFileChange={handleFileChange}
        placeholder={ingredientDetailsLabels?.UPLOAD_NUTRITION_FACTS || "Nutritional Facts Panel"}
        fileInputValue={fileSelected}
        fileInputKey={`nfp-upload-${fileInputKey}`}
        allowedTypes={[...nfpAllowedTypes]}
        toastMessage={nfpToastMessage}
        onDownload={handleDownloadFile}
        onDelete={handleRequestDeleteFile}
        darkModeClass={theme}
        htmlFor="nutritionFactPanel"
        required={ingredientDetailsFormPermissions.nutritionFactPanel.isMandatory || isNoNFPDocument}
        disabled={
          isUploadingFile ||
          (!isNoNFPDocument &&
            !ingredientDetailsFormPermissions.nutritionFactPanel?.isEditable) ||
          isFieldDisabled("nutritionFactPanel")
        }
        hideDeleteButton={downloadOnly}
      />
    );
  };

  function getMultipackDropDown():string{
    const activeForm = activeMultipackForm()
    if(activeForm?.id && activeForm?.id !== vendorId){
      return activeForm?.multipack.giftPack ? "yes": "no"
    }
    const vendorForm = vendorInformationForms?.[vendorKey ?? 0];
    return vendorForm?.multipack?.giftPack ? "yes" : "no"
  }

  function getMultipackCount():string{
    const activeForm = activeMultipackForm()
    if(activeForm?.id && vendorId !== activeForm?.id){
      return String(activeForm?.multipack.amountOfProductsInGiftPack ?? "")
    }
    const vendorForm = vendorInformationForms?.[vendorKey ?? 0];
    return String(vendorForm?.multipack?.amountOfProductsInGiftPack ?? "")
  }

  function showMultipackDropDown():boolean{
    const activeForm = activeMultipackForm()
    if(activeForm?.id && vendorId !== activeForm?.id){
      return activeForm.multipack.giftPack ?? false
    }
    const vendorForm = vendorInformationForms?.[vendorKey ?? 0];
    return vendorForm?.multipack?.giftPack ?? false
  }

  const hasFetchedIngredientGuideRef = useRef(false);
  useEffect(() => {
    if (hasFetchedIngredientGuideRef.current) return;
    hasFetchedIngredientGuideRef.current = true;
    getIngredientGuide();
  }, []);

  // Load existing NFP file if asset ID exists
  useEffect(() => {
    const loadExistingNFPFile = async () => {
      try {
        const existingNfp = ingredientDetailsForm?.nfp;
        
        if (existingNfp?.id && existingNfp?.filename) {
          console.log('📁 [IngredientDetailsForm] Loading existing NFP file:', existingNfp);
          
          // Populate the file management with existing asset ID
          const fileMap = {
            nfpDocument: {
              id: existingNfp.id,
              filename: existingNfp.filename
            }
          };
          
          populateExistingFiles(fileMap);
        }
      } catch (error) {
        console.error('❌ [IngredientDetailsForm] Error loading existing NFP file:', error);
      }
    };

    if (ingredientDetailsForm?.nfp?.id) {
      loadExistingNFPFile();
    }
    else {
      clearAllFiles();
    }
  }, [ingredientDetailsForm?.nfp?.id, ingredientDetailsForm?.nfp?.filename, populateExistingFiles]);

  const isFieldDisabled = (fieldName: string) => {
    // NFP-only mode: only nutrition fact upload stays editable; evaluate before blanket isFormDisabled
    // so callers can pass both (e.g. vendor-champion-form) without locking the NFP control.
    if (isNoNFPDocument) {
      if (fieldName === "nutritionFactPanel") return false;
      return true;
    }
    if (isFormDisabled) return true;
    if (ingredientFieldsToBeDisabled?.includes(fieldName)) return true;
    return false;
  };

  const isExistingVendorFieldsLocked = isVendorFieldsLockedForExistingVendor(
    hasExistingVendor
  );

  const activeMultipackForm = (): IVendorInformationFormData | undefined => {
    return vendorInformationForms.find(i =>(Number(i.multipack.amountOfProductsInGiftPack) > 0 ))
  }

  //const { formDisable } = useVendorForm("NFP_UPLOAD", "PDP");
// Status fields are empty in the statusesOfVendorObj, this always causes isNoNFPDocument to be false
// need to check why this is the case
  // useEffect(() => {
  //   const isDisabled = formDisable(vendorId, { status: "", workflowStatus: [] });
  //   setIsNoNFPDocument(isDisabled);
  // }, [vendorId, statusesOfVendorObj]);

  const showUnacceptableIngredients = ():boolean => {
    if (isUnacceptableIngredientsHidden) {
      return false;
    }
    const showUnacceptableIngredients =ingredientDetailsForm.unacceptableIngredients.length > 0 && !isVendor &&  (showMultipackDropDown() ? Number(getMultipackCount()) > 0 : true)
    return  showUnacceptableIngredients
  }

  const highlightUnacceptableIngredients = useCallback((index: number) => {
    if (isUnacceptableIngredientsHidden) {
      return false;
    }
    const ingredientList = String(ingredientDetailsForm?.productspecifications?.[index]?.ingredientList ?? '').split(',').map(item => item.trim().toLowerCase()).filter(Boolean);
    const unacceptable = String(ingredientDetailsForm?.unacceptableIngredients ?? '').split(',').map(item => item.trim().toLowerCase()).filter(Boolean);
    const unacceptableIngredients = unacceptable.some(item => ingredientList.includes(item));
    const showUnacceptableIngredients = unacceptableIngredients && isVendor &&  (showMultipackDropDown() ? Number(getMultipackCount()) > 0 : true)
    return  showUnacceptableIngredients
  }, [ingredientDetailsForm?.unacceptableIngredients, isVendor, showMultipackDropDown, getMultipackCount]);

  // Get NFP file payload for save operations
  const getNFPFilePayload = useCallback(() => {
    const fieldName = 'nfpDocument';
    const filesPayload = getAllFilesForPayload();
    
    if (filesPayload[fieldName]?.id) {
      return {
        id: filesPayload[fieldName].id,
        filename: filesPayload[fieldName].filename,
        type: 'document',
        data: '' // No base64 data with asset API
      };
    }
    
    return null;
  }, [getAllFilesForPayload]);

  return (
    <Box className={`form-container ${isExpanded ? "form-container-expanded" : ""}`}>
      <Box
        className={`form-header cursor-pointer ${isExpanded ? "form-header-expanded" : ""}`}
        onClick={onToggle}
      >
        <FormHeader title={formHeader} isExpanded={isExpanded} />
        <Box className="pr-4 cursor-pointer">{isExpanded ? UpArrow("dark") : DownArrow(theme)}</Box>
      </Box>
      <form onSubmit={e => e.preventDefault()}>
        {isExpanded && (
          <Box className="IngredientsDetails-forms-container">
            <Box className="field-group-ingredient-details-form">
              <Box className="field-group-ingredient-details-form-primary-section">
                {/* Vendor Name */}
                <Box>
                  <Label
                    className={`${theme} label-icon ${
                      ingredientDetailsFormErrors.ingredientDetailsError?.includes('vendorName')? "label-error-text" : ""
                    } `}
                    text={ingredientDetailsLabels?.VENDOR_NAME}
                    htmlFor="vendorName"
                    required={ingredientDetailsFormPermissions.vendorName.isMandatory}
                  />
                  <Input
                    id="vendorName"
                    name="vendorName"
                    className={`${theme} ${
                      ingredientDetailsFormErrors.ingredientDetailsError?.includes('vendorName')? "field-error-color" : ""
                    } `}
                    value={decodeHtmlEntities(ingredientDetailsForm.vendorName)}
                    onChange={handleInputChange("vendorName")}
                    disabled={
                      !ingredientDetailsFormPermissions.vendorName?.isEditable ||
                      isFieldDisabled("vendorName") ||
                      isExistingVendorFieldsLocked
                    }
                    onFocus={() => handleFieldFocus("vendorName", 0)}
                    onBlur={() => handleBlur("vendorName", 0)}
                    maxLength={Constants.VENDOR_CORPORATE_OR_VENDOR_NAME_MAX_LENGTH}
                  />
                </Box>

                {/* Nutrition Fact Panel  */}
                <Box>
                  <Label
                    className={theme}
                    text={ingredientDetailsLabels?.NUTRITION_FACT_PANEL}
                    htmlFor="nutritionFactPanel"
                    required={ingredientDetailsFormPermissions.nutritionFactPanel.isMandatory || isNoNFPDocument}
                  />
                  {renderNutritionFactPanel()}
                </Box>
              </Box>
              {showMultipackFields && (
              <Box className="field-group-ingredient-details-form-secondary-section">
                {/* Is Multi Pack Product  */}
                <Box className="field-group-multipack-radio-btn-container">
                  <Label
                    className={theme}
                    text={ingredientDetailsLabels?.IS_MULTI_PACK_PRODUCT}
                    htmlFor="isMultiPackProduct"
                    required={ingredientDetailsFormPermissions.isMultiPackProduct.isMandatory}
                  />
                  <Radio
                    id="isMultiPackProduct"
                    options={[
                      { label: "Yes", value: "yes" },
                      { label: "No", value: "no" },
                    ]}
                    name="isMultiPackProduct"
                    onChange={handleRadioChange("isMultiPackProduct")}
                    selectedValue={
                      getMultipackDropDown()
                    }
                    disabled={isMultipackDisabled || isFieldDisabled("isMultiPackProduct") || (isVendor && defaultmultipackGiftPack)}
                  />
                </Box>
                {/* How many products are in the Pack?  */}
                {showMultipackDropDown() && (
                  <Box>
                    <Label
                   className={`${theme} ${
                    ingredientDetailsFormErrors.ingredientDetailsError?.includes('howManyProductsInPack')? "label-error-text" : ""
                  } `}
                      htmlFor="howManyProductsInPack"
                      text={ingredientDetailsLabels?.NO_OF_FLAVORS_VARIETIES} 
                      required={ingredientDetailsFormPermissions.howManyProductsInPack.isMandatory}
                    />
                    <Dropdown
                        id="howManyProductsInPack"
                        className={`${theme} ${
                          ingredientDetailsFormErrors.ingredientDetailsError?.includes('howManyProductsInPack')? "label-error-text" : ""
                        } `}
                      options={[...Array.from({ length: 12 }, (_, i) => i + 1)].map(item => {
                        return { value: `${item}`, label: `${item}` };
                      })}
                      name="howManyProductsInPack"
                      selectedValue={getMultipackCount()}
                      onChange={handleDropdownChange("howManyProductsInPack")}
                      onFocus={() => handleFieldFocus("howManyProductsInPack",0)}
                      disabled={isMultipackDisabled || (isVendor && defaultmultipackGiftPack) || isFieldDisabled("howManyProductsInPack")}
                    />
                  </Box>
                )}
              </Box>
              )}
              {(showMultipackFields && showMultipackDropDown() && ingredientDetailsForm?.productspecifications) ? (
                [
                  ...Array(Number(getMultipackCount() || "0"))
                ].map((_, index) => !ingredientDetailsForm.productspecifications[index]?.delete && (
               
                  <Box
                    key={`product_${index + 1}`}
                    className="field-group-ingredient-details-form-multi-product-info"
                  >
                    {/* Product Name */}
                    <Box>
                      <Label
                        className={`${theme} label-icon ${
                          ingredientDetailsFormErrors?.productspecification?.[index]?.includes('productName')
                            ? "label-error-text"
                            : ""
                        }`}
                        text={`${ingredientDetailsLabels?.PRODUCT_NAME?.replace(
                          "{Count}",
                          String(index + 1)
                        )}`}
                        htmlFor={`productName_${index}`}
                        required={ingredientDetailsFormPermissions.productName.isMandatory}
                      />
                      <Input
                        id={`productName_${index}`}
                        name={`productName_${index}`}
                        className={`${theme} ${
                          ingredientDetailsFormErrors?.productspecification?.[index]?.includes('productName')
                            ? "field-error-color"
                            : ""
                        }`}
                        value={
                          ingredientDetailsForm?.productspecifications[index]
                            ? ingredientDetailsForm?.productspecifications[index]?.productName
                            : ""
                        } // Adjust to handle multiple values if needed
                        onChange={handleInputChange("productName", index)}
                        disabled={
                          !ingredientDetailsFormPermissions.productName?.isEditable ||
                          isFieldDisabled("productName")
                        }
                        onFocus={() => handleFieldFocus("productName", index)}
                        onBlur={() => handleBlur("productName", index)}
                        maxLength={Constants.PRODUCT_NAME_MAX_LENGTH}
                      />
                    </Box>
                    <Box className="field-group-ingredient-list-container">
                      {/* Ingredient List */}
                      <Box>
                        <Label
                          className={`${theme} label-icon ${
                            ingredientDetailsFormErrors?.productspecification?.[index]?.includes('ingredientList')
                              ? "label-error-text"
                              : ""
                          }`}
                          text={`${ingredientDetailsLabels?.INGREDIENT_LIST} ${index + 1}`}
                          htmlFor={`ingredientList_${index}`}
                          required={ingredientDetailsFormPermissions.ingredientList.isMandatory}
                        >
                           <CustomTooltip
                        tooltipText={
                          ingredientDetailsFormInfoIconMessages?.CP_VA_IDF_INGREDIENT_LIST_INFO
                        }
                      >
                        <Image src={helpIcon} alt="Help Icon" />
                      </CustomTooltip>
                        </Label>
                        <Textarea
                          id={`ingredientList_${index}`}
                          name={`ingredientList_${index}`}
                          className={`${theme} ${
                            ingredientDetailsFormErrors?.productspecification?.[index]?.includes('ingredientList')
                              ? "field-error-color"
                              : highlightUnacceptableIngredients(index) ? "field-error-color" : ""
                          }`}
                          // value={ingredientDetailsForm.ingredientList.join(",")}
                          value={
                            ingredientDetailsForm?.productspecifications[index]
                              ? ingredientDetailsForm.productspecifications[index]?.ingredientList
                              : ""
                          }
                          onKeyDown={e => e.key === "Enter" && e.preventDefault()}
                          onChange={handleInputChange("ingredientList", index)}
                          onPaste={handleIngredientPaste(index)}
                          onFocus={() => handleFieldFocus("ingredientList", index)}
                          onBlur={() => handleBlur("ingredientList", index)}
                          maxLength={Constants.INGREDIENT_LIST_MAX_LENGTH}
                          disabled={
                            !ingredientDetailsFormPermissions.ingredientList?.isEditable ||
                            isFieldDisabled("ingredientList")
                          }
                        />
                      </Box>

                      {/* Sub Ingredient List */}
                      {/* <Box>
                        <Label
                          className={`${theme} label-icon`}
                          text={ingredientDetailsLabels?.SUB_INGREDIENT_LIST}
                          htmlFor={`subIngredientList_${index}`}
                          required={ingredientDetailsFormPermissions.subIngredientList.isMandatory}
                        />
                        <Textarea
                          id={`subIngredientList_${index}`}
                          className={`${theme}`}
                          // value={ingredientDetailsForm.subIngredientList}
                          value={
                            ingredientDetailsForm?.productspecifications[index]
                              ? ingredientDetailsForm.productspecifications[index]
                                  ?.subIngredientList ?? ""
                              : ""
                          }
                          onChange={handleInputChange("subIngredientList", index)}
                          disabled={
                            !ingredientDetailsFormPermissions.subIngredientList?.isEditable ||
                            isFieldDisabled("subIngredientList")
                          }
                          onFocus={() => handleFieldFocus("subIngredientList", 0)}
                          onBlur={() => handleBlur("subIngredientList", index)}
                          maxLength={Constants.VA_INPUT_MAX_LENGTH}
                        />
                      </Box> */}
                    </Box>
                  </Box>
                )
              )
              ) : (
                // Show when isMultiPackProduct is "no" or howManyProductsInPack is not > 0
                <Box className="field-group-ingredient-details-form-multi-product-info">
                  {/* Product Name - HIDDEN when multipack = "No" */}
                  {/* Only show productName when multipack = "Yes" AND numberOfVarieties = 1 */}
                  {showMultipackFields && showMultipackDropDown() && Number(getMultipackCount()) === 1 && (
                    <Box>
                      <Label
                        className={`${theme} label-icon ${
                          ingredientDetailsFormErrors?.productspecification?.[0]?.includes('productName')
                            ? "label-error-text"
                            : ""
                        }`}
                        text={ingredientDetailsLabels?.PRODUCT_NAME?.replace("{Count}", "1") || "Product Name"}
                        htmlFor="productName_0"
                        required={ingredientDetailsFormPermissions.productName.isMandatory}
                      />
                      <Input
                        id="productName_0"
                        name="productName_0"
                        className={`${theme} ${
                          ingredientDetailsFormErrors?.productspecification?.[0]?.includes('productName')
                            ? "field-error-color"
                            : ""
                        }`}
                        value={
                          ingredientDetailsForm?.productspecifications[0]
                            ? ingredientDetailsForm?.productspecifications[0]?.productName
                            : ""
                        }
                        onChange={handleInputChange("productName", 0)}
                        disabled={
                          !ingredientDetailsFormPermissions.productName?.isEditable ||
                          isFieldDisabled("productName")
                        }
                        onFocus={() => handleFieldFocus("productName", 0)}
                        onBlur={() => handleBlur("productName", 0)}
                        maxLength={Constants.PRODUCT_NAME_MAX_LENGTH}
                      />
                    </Box>
                  )}
                  <Box className="field-group-ingredient-list-container">
                  {/* Ingredient List */}
                  <Box>
                    <Label
                      className={`${theme} label-icon ${
                        ingredientDetailsFormErrors?.productspecification?.[0]?.includes('ingredientList')
                          ? "label-error-text"
                          : ""
                      }`}
                      text={ingredientDetailsLabels?.INGREDIENT_LIST}
                      htmlFor="ingredientList"
                      required={ingredientDetailsFormPermissions.ingredientList.isMandatory}
                    >
                      <CustomTooltip
                        tooltipText={
                          ingredientDetailsFormInfoIconMessages?.CP_VA_IDF_INGREDIENT_LIST_INFO
                        }
                      >
                        <Image src={helpIcon} alt="Help Icon" />
                      </CustomTooltip>
                    </Label>
                    <Textarea
                      id="ingredientList"
                      name="ingredientList"
                      className={`${theme} ${
                        ingredientDetailsFormErrors?.productspecification?.[0]?.includes('ingredientList')
                          ? "field-error-color"
                          : highlightUnacceptableIngredients(0) ? "field-error-color" : ""
                      }`}
                      value={ingredientDetailsForm?.productspecifications[0]?.ingredientList || ""}
                      onKeyDown={e => e.key === "Enter" && e.preventDefault()}
                      onChange={handleInputChange("ingredientList")}
                      onPaste={handleIngredientPaste()}
                      onFocus={() => handleFieldFocus("ingredientList", 0)}
                      onBlur={() => handleBlur("ingredientList", 0)}
                      maxLength={Constants.INGREDIENT_LIST_MAX_LENGTH}
                      disabled={
                        !ingredientDetailsFormPermissions.ingredientList?.isEditable ||
                        isFieldDisabled("ingredientList")
                      }
                    />
                  </Box>
                  </Box>

                  {/* Sub Ingredient List */}
                  {/* <Box>
                    <Label
                      className={`${theme} label-icon`}
                      text={ingredientDetailsLabels?.SUB_INGREDIENT_LIST}
                      htmlFor="subIngredientList"
                      required={ingredientDetailsFormPermissions.subIngredientList.isMandatory}
                    />
                    <Textarea
                      id="subIngredientList"
                      className={`${theme}`}
                      value={ingredientDetailsForm.subIngredientList}
                      onChange={handleInputChange("subIngredientList")}
                      disabled={
                        !ingredientDetailsFormPermissions.subIngredientList?.isEditable ||
                        isFieldDisabled("subIngredientList")
                      }
                      onFocus={() => handleFieldFocus("subIngredientList",0)}
                      onBlur={() => handleBlur("subIngredientList", 0)}
                      maxLength={Constants.VA_INPUT_MAX_LENGTH}
                    />
                  </Box> */}
                </Box>
              )}
            </Box>

            {showUnacceptableIngredients() && (
              <>
                <Label
                  className={`${theme} section-header`}
                  text={ingredientDetailsLabels?.INGREDIENTS_VALIDATION_RESULTS}
                  htmlFor="ingredientListValidationResults"
                />
                <Box className={`${theme} underline-box field-group-ingredient-details-form`}>
                  {/* Unacceptable ingredients */}
                  <Box>
                    <div className="label-container">
                      <Image src={unAcceptableIcon} alt="Unacceptable Icon" className="help-icon" />
                      <Label
                        className={`${theme} label-icon`}
                        text={ingredientDetailsLabels?.UNACCEPTABLE_INGREDIENTS}
                        htmlFor="unacceptableIngredients"
                      />
                    </div>
                    <Textarea
                      id="unacceptableIngredients"
                      name="unacceptableIngredients"
                      className={theme}
                      value={ingredientDetailsForm.unacceptableIngredients}
                      disabled
                    />
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}
      </form>
      <ConfirmationBox
        open={deleteConfirmOpen}
        onClose={handleCancelDeleteFile}
        onDecline={handleCancelDeleteFile}
        onConfirm={handleConfirmDeleteFile}
        title={deleteDocumentConfirmTitle}
        message={deleteDocumentConfirmMessage}
        confirmText={buttonTexts?.DELETE || "Delete"}
        cancelText={buttonTexts?.CANCEL_BUTTON || "Cancel"}
        showCancelButton
      />
    </Box>
  );
};

export default IngredientDetailsForm;
